import { Layout } from '@/components/Layout';
import { useSEO } from '@/lib/useSEO';
import { Truck, MapPin, PackageCheck, Clock, ShieldCheck, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Página pública dedicada a envíos.
 * Reemplaza el ancla `/ayuda#envios` con una página independiente que cubre
 * métodos, tiempos, costos, seguimiento y responsabilidades — como pidió el
 * cliente para el Deploy inicial de AutoMarket Colombia.
 */
const SECTIONS = [
  {
    icon: Truck,
    title: 'Métodos y transportadoras',
    body:
      'Cada vendedor despacha con la transportadora que configura en AutoMarket (por ejemplo Servientrega, Interrapidísimo, Envía o Coordinadora). ' +
      'Al despachar, el vendedor registra la transportadora y el número de guía para que el comprador haga seguimiento.',
  },
  {
    icon: MapPin,
    title: 'Cobertura',
    body:
      'Cada vendedor define a qué departamentos envía y puede fijar excepciones por ciudad. Si una ciudad tiene una excepción, se aplica esa tarifa; si no, la del departamento. ' +
      'Si tu destino no está en la cobertura de un vendedor, no podrás comprarle ese pedido: el carrito te lo indicará antes de pagar para que retires sus productos o cambies la dirección.',
  },
  {
    icon: PackageCheck,
    title: 'Costo del envío',
    body:
      'El envío es una tarifa fija por pedido de cada vendedor según el destino (no depende de la cantidad de productos). Se muestra en el carrito antes de pagar, junto con el subtotal de productos y el total.\n\n' +
      'Envío gratis — se aplica una sola condición, en este orden, sin acumularse:\n' +
      '1. La zona de destino está marcada como gratis por el vendedor.\n' +
      '2. Todos los productos de ese vendedor en tu pedido tienen “Envío gratis” (si hay mezcla, se cobra la tarifa).\n' +
      '3. El subtotal de productos de ese vendedor alcanza su monto mínimo para envío gratis.\n\n' +
      'En compras con varios vendedores, cada uno calcula y cobra su envío por separado. El total que pagas con Wompi incluye productos y envíos.\n\n' +
      'Vendedores que aún no han configurado sus envíos aparecen como “Envío a convenir”: en ese caso el costo de envío NO está incluido en el total pagado y lo acuerdas directamente con el vendedor.',
  },
  {
    icon: Clock,
    title: 'Tiempos de entrega estimados',
    body:
      'Cada vendedor informa un rango de días hábiles de entrega por zona, que ves antes de pagar. ' +
      'La tarifa y el tiempo aceptados quedan guardados en tu pedido y no cambian aunque el vendedor modifique después su configuración. ' +
      'Son tiempos estimados: dependen de la transportadora, y AutoMarket no los garantiza directamente.',
  },
  {
    icon: ShieldCheck,
    title: 'Seguimiento, cancelaciones y responsabilidades',
    body:
      'Cuando el vendedor registra la guía, la verás en “Mis pedidos” con la transportadora para consultarla en su web. ' +
      'Si un pedido pagado se cancela, el reembolso incluye el valor del envío pagado. ' +
      'Si el paquete se extravía o presenta novedades, el vendedor gestiona el reclamo con la transportadora y AutoMarket acompaña el caso a través de una PQR.',
  },
];

export default function Envios() {
  useSEO({
    title: 'Envíos y tiempos de entrega | AutoMarket Colombia',
    description:
      'Todo sobre envíos en AutoMarket Colombia: transportadoras, tiempos por región, costos, seguimiento y responsabilidades vendedor/comprador.',
    canonical: '/envios',
  });

  return (
    <Layout>
      <section className="border-b border-slate-200 bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center bg-orange-500 text-slate-900">
            <Truck className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-400">Logística</div>
            <h1 className="font-display text-3xl font-black tracking-tighter" data-testid="envios-title">
              Envíos y tiempos de entrega
            </h1>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <p className="text-slate-600" data-testid="envios-intro">
          Compras seguras necesitan reglas claras de envío. Esta es la política oficial de AutoMarket Colombia para compradores y vendedores.
        </p>

        <div className="mt-10 space-y-8" data-testid="envios-sections">
          {SECTIONS.map((s) => (
            <section key={s.title} className="border-l-2 border-orange-500 pl-5">
              <div className="flex items-center gap-2">
                <s.icon className="h-5 w-5 text-slate-900" />
                <h2 className="font-display text-xl font-black tracking-tight text-slate-900">{s.title}</h2>
              </div>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-700">{s.body}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 border border-slate-200 bg-slate-50 p-6">
          <div className="flex items-center gap-2 text-slate-900 font-semibold">
            <HelpCircle className="h-5 w-5" /> ¿Un problema con tu envío?
          </div>
          <p className="mt-2 text-sm text-slate-600">
            Radica una PQR indicando el número de pedido y una descripción breve de lo que ocurrió. Nuestro equipo la asigna a un gestor y hace seguimiento hasta el cierre del caso.
          </p>
          <Link
            to="/pqr"
            data-testid="envios-cta-pqr"
            className="mt-4 inline-flex items-center gap-2 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Radicar PQR
          </Link>
        </div>
      </div>
    </Layout>
  );
}
